import React, { useEffect, useMemo, useState } from "react";
import type { CheckRow, SummaryItem } from "../types";
import { api } from "../api";
import { Badge } from "./Badge";
import { ChecksBars, createTimeSlots } from "./ChecksBars";

function fmt(tsUnix?: number | null) {
  if (!tsUnix) return "—";
  return new Date(tsUnix * 1000).toLocaleString();
}

export function EndpointCard({ item }: { item: SummaryItem }) {
  const ep = item.endpoint;
  const last = item.last;

  const [checks, setChecks] = useState<CheckRow[]>([]);
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState("");
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadChecks = () => {
      setErr("");
      api.checks(ep.id, 80)
        .then((r) => mounted && setChecks(r.checks || []))
        .catch((e) => mounted && setErr(String(e?.message || e)));
    };

    loadChecks();
    const interval = window.setInterval(loadChecks, 60_000);

    return () => {
      mounted = false;
      window.clearInterval(interval);
    };
  }, [ep.id]);

  const okNow = Boolean(last?.ok);
  const target = ep.url || ep.addr || "";
  const subtitle = useMemo(() => {
    if (!last) return "No data yet";
    return `code ${last.status_code || "—"} • ${last.latency_ms || "—"}ms • last ${fmt(last.ts_unix)}`;
  }, [last]);

  const displayLimit = windowWidth >= 1000 ? 70 : 30;
  const slots = useMemo(() => createTimeSlots(checks, displayLimit), [checks, displayLimit]);

  return (
    <div className="card">
      <div className="cardTop">
        <div className="left">
          <div className="titleRow">
            <div className="title">{ep.name}</div>
            <Badge ok={okNow} />
          </div>
          <div className="target">{target}</div>
          <div className="subtitle">{subtitle}</div>
        </div>

        <div className="right">
          <button className="btn" onClick={() => setOpen((v) => !v)}>
            {open ? "Hide" : "Details"}
          </button>
        </div>
      </div>

      <div className="cardBody">
        {err ? <div className="error">checks load failed: {err}</div> : <ChecksBars checks={checks} />}
      </div>

      {open && (
        <div className="details">
          <div className="detailsHeader">Recent checks (latest first)</div>
          <div className="table">
            <div className="thead">
              <div>Time</div><div>OK</div><div>Code</div><div>Latency</div>
            </div>
            {[...slots].reverse().map((slot) => (
              <div className="trow" key={slot.id}>
                <div>{fmt(slot.tsUnix)}</div>
                <div className={slot.check ? (slot.check.ok ? "okText" : "downText") : "muted"}>
                  {slot.check ? (slot.check.ok ? "OK" : "DOWN") : "No Data"}
                </div>
                <div>{slot.check?.status_code || "—"}</div>
                <div>{slot.check?.latency_ms ? `${slot.check.latency_ms}ms` : "—"}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
